import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';

import globalTranslations from '../../../../../i18n/globalTranslations';
import OrderCheckoutProduct from '../../../../../models/order/checkout/OrderCheckoutProduct';
import { modelOf } from '../../../../../prop-types';
import AccountStore from '../../../../../store/AccountStore';
import CheckoutStore from '../../../../../store/CheckoutStore';
import Price from '../../../../product/Price';

export const PART_UNIT = 'unit';
export const PART_TOTAL = 'total';
export const PART_BOTH = 'both';

const PART_VALUES = [PART_UNIT, PART_TOTAL, PART_BOTH];

const showsUnit = (part) => part === PART_UNIT || part === PART_BOTH;
const showsTotal = (part) => part === PART_TOTAL || part === PART_BOTH;

/**
 * Renders price block for checkout cart product row.
 * - B2C: only total (unit price is shown in product details).
 * - B2B: unit price (with optional VAT %) + total.
 * Uses *_formatted strings; display follows Account.user.show_prices_with_tax.
 */
const zeroFormatted = (intl) =>
  intl.formatNumber(0, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

const CheckoutCartProductPrice = ({
  accountStore,
  checkoutStore,
  product,
  part = PART_BOTH,
  intl,
}) => {
  const isB2B = checkoutStore.order?.isB2BCheckout ?? false;

  const unitPrice = product.unit_price_formatted;
  const unitNormalPrice = product.unit_normal_price_formatted;
  const totalPrice = product.total_price_formatted;

  const formatTaxRate = () => {
    if (!isB2B) return null;
    const raw = product.tax_rate;
    const numeric = typeof raw === 'string' ? Number(raw) : Number(raw);
    if (!Number.isFinite(numeric)) return null;
    return intl.formatNumber(numeric, {
      minimumFractionDigits: numeric % 1 === 0 ? 0 : 1,
      maximumFractionDigits: 2,
    });
  };

  const taxRate = formatTaxRate();
  const displayVatRate = accountStore.showPricesWithTax
    ? taxRate != null
      ? taxRate
      : zeroFormatted(intl)
    : zeroFormatted(intl);

  return (
    <>
      {isB2B && showsUnit(part) && (
        <div className="CheckoutCartProductRow__unit-price-block">
          <div className="CheckoutCartProductRow__unit-price-line">
            <Price
              className="CheckoutCartProductRow__product-price"
              price={unitPrice}
              discount={product.has_discount}
            />
            <span className="CheckoutCartProductRow__vat-rate">
              {' '}
              (
              <FormattedMessage {...globalTranslations.vatShort} />{' '}
              {displayVatRate}
              %)
            </span>
          </div>
          {product.has_discount && (
            <Price
              price={unitNormalPrice}
              muted
              fontSize
              prefix={
                <>
                  <FormattedMessage {...globalTranslations.RRP} />{' '}
                </>
              }
              className="CheckoutCartProductRow__normal-product-price"
            />
          )}
        </div>
      )}
      {showsTotal(part) && (
        <div className="CheckoutCartProductRow__total-price">
          <FormattedMessage
            {...globalTranslations.totalPriceSentenceAbbr}
            values={{ value: <Price price={totalPrice} /> }}
          />
        </div>
      )}
    </>
  );
};

CheckoutCartProductPrice.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  checkoutStore: modelOf(CheckoutStore).isRequired,
  product: modelOf(OrderCheckoutProduct).isRequired,
  part: PropTypes.oneOf(PART_VALUES),
};

export default injectIntl(
  inject('accountStore', 'checkoutStore')(observer(CheckoutCartProductPrice))
);
